import "../../styles/common.scss";

import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import CircularProgress from "@mui/material/CircularProgress";
import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import React from "react";
// icons
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { AvatarSize } from "../../components/common/Avatar";
import PrimaryButton from "../../components/common/PrimaryButton";
import ProfileCard from "../../components/common/profileCard";
import { getContracts } from "../../setup/api/services/contract";
import { IContractDetailsResponseData } from "../../setup/api/services/contract/models";
import { getBasicUserDetailsById } from "../../setup/api/services/user";
// apis
import {
  IUserInfoResponse,
  IUserInfoResponseData,
} from "../../setup/api/services/user/models";
import { ROUTES } from "../../setup/constants";
import { AuthConfig } from "../../setup/context/config/authConfig";
// constants
import { HOME } from "./constants";
import Footer from "./footer";
import homeStyles from "./home.module.scss";
import NewTransaction from "./newtransaction";
import OnboardingCard from "./onboardingCard";
import Transaction from "./transaction";

const Home = () => {
  const userInfo = AuthConfig.getUserInfo();
  const userId = userInfo?.id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  // getting parameters for title and prize
  const location = useLocation();
  const searchParams = useMemo(() => {
    return new URLSearchParams(location?.search);
  }, [location?.search]);

  const title = useMemo(() => {
    const titleLink = searchParams?.get("Title");
    if (titleLink) {
      return titleLink;
    } else {
      return undefined;
    }
  }, [searchParams]);

  const price = useMemo(() => {
    const priceLink = searchParams?.get("Price");
    if (priceLink) {
      return Number(priceLink);
    } else {
      return undefined;
    }
  }, [searchParams]);

  const [userDetails, setUserDetials] = useState<
    IUserInfoResponseData | undefined
  >(undefined);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [contracts, setContracts] = useState<IContractDetailsResponseData[]>(
    []
  );

  useEffect(() => {
    if (title && price) {
      setShowModal(true);
    }
  }, [title, price]);

  const [contractLoading, setContractLoading] = useState<boolean>(false);

  const fetchUserBasicInfo = useCallback(async () => {
    try {
      if (userId) {
        const response: IUserInfoResponse = await getBasicUserDetailsById(
          userId
        );
        if (response) {
          setUserDetials(response?.data);
        }
      }
    } catch (error) {
      Sentry.captureException(error);
      console.error(error);
    }
  }, [userId]);

  useEffect(() => {
    fetchUserBasicInfo().catch(console.error);
  }, [fetchUserBasicInfo]);

  const handleModal = useCallback(() => {
    setShowModal(!showModal);
  }, [showModal]);

  const fetchContracts = useCallback(async () => {
    setContractLoading(true);
    try {
      const body = {
        listType: "active",
        page: 0,
        size: 3,
      };
      const response = await getContracts(body);
      setContracts(response?.data?.content);
      setContractLoading(false);
    } catch (error) {
      Sentry.captureException(error);
      console.error(error);
      setContractLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);
  const onClose = () => {
    setShowModal(false);
    fetchContracts();
    navigate(ROUTES.HOME);
  };

  const onProfileClick = () => {
    navigate(`${ROUTES.PROFILE}`);
  };
  return (
    <div className={homeStyles["container"]}>
      <div className={"page_layout"}>
        <div className={homeStyles.section_container}>
          <div className={homeStyles.left_section}>
            {userInfo && (
              <>
                <div className={classNames("card", "mb2")}>
                  <ProfileCard
                    name={
                      userDetails?.profile?.fullName
                        ? `${userDetails?.profile?.fullName}`
                        : ""
                    }
                    welcomeText={
                      userDetails?.profile?.fullName ? `${HOME.WELCOME}` : ""
                    }
                    phoneNumber={userInfo?.phoneNumber || ""}
                    profile={userDetails?.profile?.photoUrl || ""}
                    avatarSize={AvatarSize.MEDIUM}
                    onClick={onProfileClick}
                  />
                </div>
                <div className={"mb2r"}>
                  <PrimaryButton
                    id={`home_create_contract`}
                    onClick={handleModal}
                    Lefticon={
                      <AddCircleRoundedIcon
                        fontSize="large"
                        className={homeStyles["profile_button_icon"]}
                      />
                    }>
                    {t("lbl_home_start_new_transaction")}
                  </PrimaryButton>
                </div>
              </>
            )}
            <OnboardingCard />
            <Footer />
          </div>
          <div className={homeStyles.right_section}>
            {contractLoading ? (
              <div className={homeStyles.empty_container}>
                <CircularProgress />
              </div>
            ) : (
              <Transaction
                transactions={contracts}
                handleNewTransaction={handleModal}
              />
            )}
          </div>
        </div>
      </div>
      <NewTransaction
        showModal={showModal}
        titleLink={title}
        priceLink={price}
        onClose={onClose}
      />
    </div>
  );
};

export default Home;
